/* eslint-disable no-console */
import { storage } from "@/firebase";
import {
  ref,
  deleteObject,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  getMetadata,
  updateMetadata,
  listAll
} from "firebase/storage";

const state = {
  uploadProgress: {},
  uploadedFiles: [],
  error: null,
  loading: false
};

const actions = {
  /**
   * Delete media files from Firebase Storage
   * Handles small, medium, and large versions of images
   */
  async deleteMediaFilesFromStorage({ commit }, image) {
    if (!image) {
      throw new Error("Image data is required");
    }

    commit("setMediaError", null);
    commit("setLoading", true);

    try {
      console.log("Deleting image files:", image.smallStoragePath);

      const deletePromises = [];

      // Create storage references and delete files
      if (image.smallStoragePath) {
        const smallRef = ref(storage, image.smallStoragePath);
        deletePromises.push(
          deleteObject(smallRef).catch(error => {
            console.warn("Error deleting small image:", error);
            // Don't throw - file might not exist
          })
        );
      }

      if (image.mediumStoragePath) {
        const mediumRef = ref(storage, image.mediumStoragePath);
        deletePromises.push(
          deleteObject(mediumRef).catch(error => {
            console.warn("Error deleting medium image:", error);
            // Don't throw - file might not exist
          })
        );
      }

      if (image.largeStoragePath) {
        const largeRef = ref(storage, image.largeStoragePath);
        deletePromises.push(
          deleteObject(largeRef).catch(error => {
            console.warn("Error deleting large image:", error);
            // Don't throw - file might not exist
          })
        );
      }

      // Wait for all deletions to complete
      await Promise.all(deletePromises);

      console.log("Successfully deleted image files");
      return "ok";
    } catch (error) {
      console.error("Error deleting media files:", error);
      commit("setMediaError", error.message);
      throw new Error("Error when deleting image files");
    } finally {
      commit("setLoading", false);
    }
  },

  /**
   * Upload a single file to Firebase Storage
   */
  async uploadFile({ commit }, { file, path, metadata = {} }) {
    if (!file) {
      throw new Error("File is required");
    }

    if (!path) {
      throw new Error("Storage path is required");
    }

    commit("setMediaError", null);
    commit("setLoading", true);

    try {
      const storageRef = ref(storage, path);

      // Use resumable upload for larger files
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          snapshot => {
            // Track upload progress
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            commit("setUploadProgress", { path, progress });
            console.log(`Upload is ${progress.toFixed(2)}% done`);
          },
          error => {
            console.error("Upload error:", error);
            commit("setMediaError", error.message);
            reject(error);
          },
          async () => {
            try {
              // Upload completed successfully
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const fileMetadata = await getMetadata(uploadTask.snapshot.ref);

              const uploadedFile = {
                name: file.name,
                path,
                downloadURL,
                size: fileMetadata.size,
                contentType: fileMetadata.contentType,
                timeCreated: fileMetadata.timeCreated,
                updated: fileMetadata.updated
              };

              commit("addUploadedFile", uploadedFile);
              commit("clearUploadProgress", path);
              commit("setLoading", false);

              console.log("File uploaded successfully:", downloadURL);
              resolve(uploadedFile);
            } catch (error) {
              console.error("Error getting download URL:", error);
              commit("setMediaError", error.message);
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error("Error starting upload:", error);
      commit("setMediaError", error.message);
      commit("setLoading", false);
      throw error;
    }
  },

  /**
   * Upload multiple files to Firebase Storage
   */
  async uploadMultipleFiles(
    { dispatch, commit },
    { files, basePath, metadata = {} }
  ) {
    if (!files || !Array.isArray(files) || files.length === 0) {
      throw new Error("Files array is required");
    }

    if (!basePath) {
      throw new Error("Base storage path is required");
    }

    commit("setMediaError", null);
    commit("setLoading", true);

    try {
      const uploadPromises = files.map((file, index) => {
        const fileName = file.name || `file_${index}`;
        const filePath = `${basePath}/${fileName}`;

        return dispatch("uploadFile", {
          file,
          path: filePath,
          metadata
        });
      });

      const results = await Promise.all(uploadPromises);
      console.log(`Successfully uploaded ${results.length} files`);
      return results;
    } catch (error) {
      console.error("Error uploading multiple files:", error);
      commit("setMediaError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  /**
   * Delete a single file from Firebase Storage
   */
  async deleteFile({ commit }, filePath) {
    if (!filePath) {
      throw new Error("File path is required");
    }

    commit("setMediaError", null);

    try {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);

      commit("removeUploadedFile", filePath);
      console.log("File deleted successfully:", filePath);
      return true;
    } catch (error) {
      console.error("Error deleting file:", error);
      commit("setMediaError", error.message);
      throw error;
    }
  },

  /**
   * Get file metadata from Firebase Storage
   */
  async getFileMetadata({ commit }, filePath) {
    if (!filePath) {
      throw new Error("File path is required");
    }

    try {
      const fileRef = ref(storage, filePath);
      const metadata = await getMetadata(fileRef);

      return {
        name: metadata.name,
        bucket: metadata.bucket,
        fullPath: metadata.fullPath,
        size: metadata.size,
        contentType: metadata.contentType,
        timeCreated: metadata.timeCreated,
        updated: metadata.updated,
        downloadTokens: metadata.downloadTokens
      };
    } catch (error) {
      console.error("Error getting file metadata:", error);
      commit("setMediaError", error.message);
      throw error;
    }
  },

  /**
   * Get download URL for a file
   */
  async getDownloadUrl({ commit }, filePath) {
    if (!filePath) {
      throw new Error("File path is required");
    }

    try {
      const fileRef = ref(storage, filePath);
      const downloadURL = await getDownloadURL(fileRef);

      console.log("Download URL retrieved:", downloadURL);
      return downloadURL;
    } catch (error) {
      console.error("Error getting download URL:", error);
      commit("setMediaError", error.message);
      throw error;
    }
  },

  /**
   * List all files in a storage directory
   */
  async listFiles({ commit }, folderPath) {
    if (!folderPath) {
      throw new Error("Folder path is required");
    }

    commit("setMediaError", null);
    commit("setLoading", true);

    try {
      const folderRef = ref(storage, folderPath);
      const result = await listAll(folderRef);

      const files = await Promise.all(
        result.items.map(async itemRef => {
          try {
            const [downloadURL, metadata] = await Promise.all([
              getDownloadURL(itemRef),
              getMetadata(itemRef)
            ]);

            return {
              name: itemRef.name,
              fullPath: itemRef.fullPath,
              downloadURL,
              size: metadata.size,
              contentType: metadata.contentType,
              timeCreated: metadata.timeCreated,
              updated: metadata.updated
            };
          } catch (error) {
            console.warn(`Error processing file ${itemRef.name}:`, error);
            return null;
          }
        })
      );

      // Filter out null entries (failed file processing)
      const validFiles = files.filter(file => file !== null);

      console.log(`Found ${validFiles.length} files in ${folderPath}`);
      return validFiles;
    } catch (error) {
      console.error("Error listing files:", error);
      commit("setMediaError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  /**
   * Update file metadata
   */
  async updateFileMetadata({ commit }, { filePath, metadata }) {
    if (!filePath) {
      throw new Error("File path is required");
    }

    if (!metadata) {
      throw new Error("Metadata is required");
    }

    try {
      const fileRef = ref(storage, filePath);
      const updatedMetadata = await updateMetadata(fileRef, metadata);

      console.log("File metadata updated successfully");
      return updatedMetadata;
    } catch (error) {
      console.error("Error updating file metadata:", error);
      commit("setMediaError", error.message);
      throw error;
    }
  },

  /**
   * Clear upload progress for a specific file
   */
  clearUploadProgress({ commit }, filePath) {
    commit("clearUploadProgress", filePath);
  },

  /**
   * Clear all upload progress
   */
  clearAllUploadProgress({ commit }) {
    commit("clearAllUploadProgress");
  },

  /**
   * Clear uploaded files list
   */
  clearUploadedFiles({ commit }) {
    commit("clearUploadedFiles");
  },

  /**
   * Clear error state
   */
  clearMediaError({ commit }) {
    commit("setMediaError", null);
  }
};

const mutations = {
  setMediaError(state, error) {
    state.error = error;
  },

  setLoading(state, loading) {
    state.loading = Boolean(loading);
  },

  setUploadProgress(state, { path, progress }) {
    state.uploadProgress = {
      ...state.uploadProgress,
      [path]: progress
    };
  },

  clearUploadProgress(state, path) {
    const { [path]: removed, ...remaining } = state.uploadProgress;
    state.uploadProgress = remaining;
  },

  clearAllUploadProgress(state) {
    state.uploadProgress = {};
  },

  addUploadedFile(state, file) {
    state.uploadedFiles = [...state.uploadedFiles, file];
  },

  removeUploadedFile(state, filePath) {
    state.uploadedFiles = state.uploadedFiles.filter(
      file => file.path !== filePath
    );
  },

  clearUploadedFiles(state) {
    state.uploadedFiles = [];
  }
};

const getters = {
  /**
   * Get current error state
   */
  mediaError(state) {
    return state.error;
  },

  /**
   * Get current loading state
   */
  mediaIsLoading(state) {
    return state.loading;
  },

  /**
   * Get upload progress for a specific file
   */
  getUploadProgress: state => filePath => {
    return state.uploadProgress[filePath] || 0;
  },

  /**
   * Get all upload progress
   */
  allUploadProgress(state) {
    return state.uploadProgress;
  },

  /**
   * Check if any uploads are in progress
   */
  hasActiveUploads(state) {
    return Object.keys(state.uploadProgress).length > 0;
  },

  /**
   * Get all uploaded files
   */
  uploadedFiles(state) {
    return state.uploadedFiles;
  },

  /**
   * Get uploaded files count
   */
  uploadedFilesCount(state) {
    return state.uploadedFiles.length;
  },

  /**
   * Get uploaded file by path
   */
  getUploadedFileByPath: state => filePath => {
    return state.uploadedFiles.find(file => file.path === filePath) || null;
  },

  /**
   * Get uploaded files by content type
   */
  getUploadedFilesByType: state => contentType => {
    return state.uploadedFiles.filter(
      file => file.contentType && file.contentType.includes(contentType)
    );
  },

  /**
   * Get image files only
   */
  uploadedImages(state) {
    return state.uploadedFiles.filter(
      file => file.contentType && file.contentType.startsWith("image/")
    );
  },

  /**
   * Get total size of uploaded files
   */
  totalUploadedSize(state) {
    return state.uploadedFiles.reduce(
      (total, file) => total + (file.size || 0),
      0
    );
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
